import React, { Fragment, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import EmailText from './emailText';
import PresentationHeading from './presentation-heading';
import EmailAvatar from './email-avatar';
import EmailHeader from './email-header';
import EmailAttachment from './email-attachment';
import EmailAttachmentHover from './email-attachment-hover';
import EmailLinksAlert from './email-links-alert';
import InstructionsOverlay from './instructions-overlay';
import { FlexDiv } from '../../design-system/containers';
import './presentation.scss';
import './assessment.scss';
import './questions.scss';

export const EmailQuizGeneral = observer(
    ({ ui, onAction, userEmail, userName, isInAnswerMode = false }) => {
        const { t } = useTranslation('assessment');
        const [isShowingDetails, setIsShowingDetails] = useState(false);
        const [isShowingLinkAlert, setIsShowingLinkAlert] = useState(false);
        const [isHoveringOnAttachment, setIsHoveringOnAttachment] =
            useState(false);
        const [hoverStartTime, setHoverStartTime] = useState(null);
        const [instructionsOverlayOpen, setInstructionsOverlayOpen] = useState(
            ui.email.showInstructionsOverlay && !isInAnswerMode
        );

        const address = ui.address;
        const attachment = ui.attachment;
        const link = ui.link;

        const handleShowDetails = () => {
            if (address && address.clickActionId && !isShowingDetails) {
                onAction && onAction(address.clickActionId);
            }
            setIsShowingDetails(!isShowingDetails);
        };

        const handleAttachmentMouseEnter = () => {
            setIsHoveringOnAttachment(true);
            startHover();
        };

        const handleAttachmentMouseLeave = () => {
            setIsHoveringOnAttachment(false);
            endHover();
        };

        const handleAttachmentClick = () => {
            if (attachment && attachment.clickActionId) {
                onAction && onAction(attachment.clickActionId);
            }
            setIsShowingLinkAlert(true);
        };

        const handleLinkMouseEnter = () => {
            startHover();
        };

        const handleLinkMouseLeave = (hoverActionId) => {
            endHover(hoverActionId);
        };

        const handleLinkClick = (clickActionId) => {
            if (clickActionId) {
                onAction && onAction(clickActionId);
            }
            setIsShowingLinkAlert(true);
        };

        const startHover = () => {
            setHoverStartTime(new Date());
        };

        const endHover = (hoverActionId) => {
            let hoverEnd = new Date();
            // Only fire the action if user hovers over link for more than 2 seconds
            if (hoverEnd - hoverStartTime > 2000) {
                hoverActionId && onAction && onAction(hoverActionId);
            }
        };

        return (
            <Fragment>
                <div className="assessment-presentation">
                    <div className="question">
                        <PresentationHeading ui={ui} t={t} />
                        <div className="email-gmail">
                            {instructionsOverlayOpen && (
                                <InstructionsOverlay
                                    closeModal={() =>
                                        setInstructionsOverlayOpen(false)
                                    }
                                    t={t}
                                />
                            )}
                            <FlexDiv $noWrap gap={8}>
                                <EmailAvatar ui={ui} desktopOnly />
                                <FlexDiv column>
                                    <EmailHeader
                                        ui={ui}
                                        t={t}
                                        fromName={t(ui.email.from_name)}
                                        fromEmail={t(ui.email.from_address)}
                                        toEmail={ui.email.to_address}
                                        userEmail={
                                            ui.email.to_address || userEmail
                                        }
                                        userName={ui.email.to_name || userName}
                                        subject={t(ui.email.subject)}
                                        toExtraEmail={ui.email.extra_to_string}
                                        onShowDetails={handleShowDetails}
                                    />
                                    <EmailText
                                        ui={ui}
                                        userName={userName}
                                        handleLinkClick={handleLinkClick}
                                        handleLinkMouseLeave={
                                            handleLinkMouseLeave
                                        }
                                        handleLinkMouseEnter={
                                            handleLinkMouseEnter
                                        }
                                    />

                                    {attachment && (
                                        <FlexDiv borderTop mt={16}>
                                            <EmailAttachment
                                                isHoveringOnAttachment={
                                                    isHoveringOnAttachment
                                                }
                                                attachment={attachment}
                                                t={t}
                                                handleAttachmentClick={
                                                    handleAttachmentClick
                                                }
                                                handleAttachmentMouseEnter={
                                                    handleAttachmentMouseEnter
                                                }
                                                handleAttachmentMouseLeave={
                                                    handleAttachmentMouseLeave
                                                }
                                            />
                                        </FlexDiv>
                                    )}
                                </FlexDiv>
                            </FlexDiv>
                            {attachment && attachment.href && (
                                <EmailAttachmentHover
                                    isHoveringOnAttachment={
                                        isHoveringOnAttachment
                                    }
                                    attachment={attachment}
                                />
                            )}
                            {(attachment || link) && (
                                <EmailLinksAlert
                                    isShowingLinkAlert={isShowingLinkAlert}
                                    t={t}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
);
